import { Grid } from '@mui/material';
import { SMPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import MenuBar, { MenuHeader, MenuSection } from '../../components/new-layout/MenuBar';
import PageLayout from '../../components/new-layout/PageLayout';
import * as routing from '../../constants/Routing';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Text from '../../react-components/Text';
import { DriversApplication } from './application';
import { DriversVehicles } from './vehicles';

export const Drivers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: activeProject } = useRetrieveActiveProject();
  const [isMenuBarOpen, setIsMenuBarOpen] = useState<boolean>(true);

  if (!activeProject) {
    return null;
  }

  const hasPrivileges = () => userCredentials?.projects.test(activeProject.id, `can_manage_drivers_app`);

  return (
    <Routes>
      <Route
        path="*"
        element={
          <PageLayout style={{ overflow: `hidden` }}>
            <>
              <MenuBar
                onCollapseChange={(collapsed: boolean) => setIsMenuBarOpen(collapsed)}
                content={
                  <div>
                    <MenuHeader>
                      <Grid container direction="row" alignItems="center" justifyContent="space-between">
                        <Text variant="h6" intlId="drivers.menubar.title" />
                      </Grid>
                    </MenuHeader>

                    <MenuSection
                      id="drivers.menubar.vehicles"
                      textId="drivers-menubar-vehicles"
                      textIntlId="drivers.menubar.vehicles"
                      textStyle={{ fontWeight: `bold` }}
                      active={location.pathname === routing.DRIVERS.VEHICLES.replace(`:projectId`, activeProject.id)}
                      onClick={() => {
                        navigate(routing.DRIVERS.VEHICLES.replace(`:projectId`, activeProject.id));
                      }}
                    />

                    {hasPrivileges() && (
                      <>
                        <VSpacer small />

                        <MenuSection
                          id="drivers.menubar.app.stop-fields"
                          textId="drivers-menubar-app-stop-fields"
                          textIntlId="drivers.menubar.app.stop-fields"
                          textStyle={{ fontWeight: `bold` }}
                          active={
                            location.pathname ===
                            routing.DRIVERS.APP.STOP_FIELDS.replace(`:projectId`, activeProject.id)
                          }
                          onClick={() => {
                            navigate(routing.DRIVERS.APP.STOP_FIELDS.replace(`:projectId`, activeProject.id));
                          }}
                        />

                        <MenuSection
                          id="drivers.menubar.pod"
                          textId="drivers-menubar-pod"
                          textIntlId="drivers.menubar.pod"
                          textStyle={{ fontWeight: `bold` }}
                          active={
                            location.pathname ===
                              routing.DRIVERS.APP.POD.COMPLETED.replace(`:projectId`, activeProject.id) ||
                            location.pathname ===
                              routing.DRIVERS.APP.POD.CANCELED.replace(`:projectId`, activeProject.id)
                          }
                          onClick={() => {
                            navigate(routing.DRIVERS.APP.POD.ROOT.replace(`:projectId`, activeProject.id));
                          }}
                        />

                        <MenuSection
                          id="drivers.menubar.pod.completed"
                          textId="drivers-menubar-pod-completed"
                          textIntlId="drivers.menubar.pod.completed"
                          indent={1}
                          active={
                            location.pathname ===
                            routing.DRIVERS.APP.POD.COMPLETED.replace(`:projectId`, activeProject.id)
                          }
                          onClick={() => {
                            navigate(routing.DRIVERS.APP.POD.COMPLETED.replace(`:projectId`, activeProject.id));
                          }}
                        />

                        <MenuSection
                          id="drivers.menubar.pod.canceled"
                          textId="drivers-menubar-pod-canceled"
                          textIntlId="drivers.menubar.pod.canceled"
                          indent={1}
                          active={
                            location.pathname ===
                            routing.DRIVERS.APP.POD.CANCELED.replace(`:projectId`, activeProject.id)
                          }
                          onClick={() => {
                            navigate(routing.DRIVERS.APP.POD.CANCELED.replace(`:projectId`, activeProject.id));
                          }}
                        />

                        <MenuSection
                          id="drivers.menubar.app.configuration"
                          textId="drivers-menubar-app-configuration"
                          textIntlId="drivers.menubar.app.configuration"
                          textStyle={{ fontWeight: `bold` }}
                          active={
                            location.pathname ===
                            routing.DRIVERS.APP.CONFIGURATION.replace(`:projectId`, activeProject.id)
                          }
                          onClick={() => {
                            navigate(routing.DRIVERS.APP.CONFIGURATION.replace(`:projectId`, activeProject.id));
                          }}
                        />
                      </>
                    )}
                  </div>
                }
              />

              <Grid
                container
                direction="column"
                wrap="nowrap"
                style={{
                  zIndex: 1,
                  height: `100%`,
                  width: `calc(100% - ${isMenuBarOpen ? `260px` : `20px`})`,
                  backgroundColor: SMPalette.grey2,
                }}
              >
                <Routes>
                  <Route
                    index
                    element={<Navigate to={routing.DRIVERS.VEHICLES.replace(`:projectId`, activeProject.id)} />}
                  />

                  <Route path="/vehicles" element={<DriversVehicles />} />

                  {hasPrivileges() && <Route path="/app/*" element={<DriversApplication project={activeProject} />} />}

                  <Route
                    path="*"
                    element={<Navigate to={routing.DRIVERS.VEHICLES.replace(`:projectId`, activeProject.id)} />}
                  />
                </Routes>
              </Grid>
            </>
          </PageLayout>
        }
      />
    </Routes>
  );
};
